import { useEffect, useState } from "react";
import { Gift } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { apiFetch } from "@/lib/api";
import { formatBRL } from "@/lib/format";
import { useToast } from "@/components/ui/Toast";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Chip } from "@/components/ui/Chip";
import { Field } from "@/components/ui/Field";
import { ScreenSpinner } from "@/components/ui/Spinner";
import { AdminHeader, canManageUsers, errMessage } from "./shared";

type WelcomeCreditState = {
  amount: number;
  expiresOn: string | null;
  active: boolean;
};

// Crédito de boas-vindas: o admin define um valor e uma data de expiração;
// todo usuário que se cadastrar até 23:59 (America/Sao_Paulo) da data recebe
// o valor em créditos de plataforma no ato do cadastro.
export default function AdminBoasVindasPage() {
  const { user: viewer } = useAuth();
  const { showToast } = useToast();
  const [config, setConfig] = useState<WelcomeCreditState | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [amountStr, setAmountStr] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    apiFetch<WelcomeCreditState>("/api/admin/welcome-credit")
      .then((c) => {
        setConfig(c);
        setAmountStr(c.amount > 0 ? String(c.amount) : "");
        setExpiresOn(c.expiresOn ?? "");
      })
      .catch((err) => setError(errMessage(err)));
  }, []);

  if (!viewer) return null;
  const canWrite = canManageUsers(viewer.role);

  const save = async (payload: { amount: number; expiresOn: string | null }) => {
    setSaving(true);
    try {
      const next = await apiFetch<WelcomeCreditState>(
        "/api/admin/welcome-credit",
        { method: "PATCH", body: JSON.stringify(payload) },
      );
      setConfig(next);
      setAmountStr(next.amount > 0 ? String(next.amount) : "");
      setExpiresOn(next.expiresOn ?? "");
      showToast({
        type: "success",
        message: next.active
          ? "Campanha de boas-vindas ativa."
          : "Campanha de boas-vindas desativada.",
      });
    } catch (err) {
      showToast({ type: "error", message: errMessage(err) });
    } finally {
      setSaving(false);
    }
  };

  const amount = Number.parseInt(amountStr, 10);
  const validToSave =
    Number.isFinite(amount) && amount > 0 && expiresOn.trim() !== "";

  return (
    <div className="flex flex-col gap-4 px-5 pt-6">
      <AdminHeader
        title="Boas-vindas"
        caption="Créditos automáticos para novos cadastros"
      />

      {error && (
        <Card className="p-4 text-center text-sm text-[#ffb4ad]">{error}</Card>
      )}
      {!config && !error && <ScreenSpinner />}

      {config && (
        <>
          <Card className="flex items-center gap-3 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-(--radius-field) bg-secondary">
              <Gift size={18} className="text-tint" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold">
                {config.active
                  ? `${formatBRL(config.amount)} em créditos por cadastro`
                  : "Nenhuma campanha ativa"}
              </p>
              <p className="text-xs text-muted-foreground">
                {config.active && config.expiresOn
                  ? `Válida para cadastros até ${new Date(
                      `${config.expiresOn}T00:00:00Z`,
                    ).toLocaleDateString("pt-BR", { timeZone: "UTC" })}, 23:59`
                  : "Novos cadastros começam com saldo zerado."}
              </p>
            </div>
            <Chip tone={config.active ? "success" : "muted"}>
              {config.active ? "ATIVA" : "INATIVA"}
            </Chip>
          </Card>

          <Card className="flex flex-col gap-3 p-5">
            <Field
              label="Valor em créditos (R$)"
              value={amountStr}
              onChange={(e) =>
                setAmountStr(e.target.value.replace(/[^0-9]/g, ""))
              }
              inputMode="numeric"
              placeholder="Ex.: 500"
              disabled={!canWrite || saving}
            />
            <Field
              label="Cadastros até (23:59 do dia, horário de Brasília)"
              type="date"
              value={expiresOn}
              onChange={(e) => setExpiresOn(e.target.value)}
              disabled={!canWrite || saving}
            />
            <p className="text-[11px] text-muted-foreground">
              Todo usuário que se cadastrar até as 23:59 da data acima recebe o
              valor em créditos de plataforma — apostáveis, nunca sacáveis
              (prêmios continuam sendo pagos em dinheiro). O crédito entra no
              extrato como "Crédito de boas-vindas".
            </p>
            {canWrite && (
              <div className="flex gap-2">
                {config.active && (
                  <Button
                    title="Desativar"
                    variant="ghost"
                    disabled={saving}
                    onClick={() => void save({ amount: 0, expiresOn: null })}
                    className="flex-1 border-destructive/50 text-[#ffb4ad] hover:bg-destructive/10"
                  />
                )}
                <Button
                  title="Salvar campanha"
                  loading={saving}
                  disabled={!validToSave}
                  onClick={() =>
                    void save({ amount, expiresOn: expiresOn.trim() })
                  }
                  className="flex-1"
                />
              </div>
            )}
            {!canWrite && (
              <p className="text-xs text-muted-foreground">
                Modo somente leitura — seu papel não permite alterar a
                campanha.
              </p>
            )}
          </Card>
        </>
      )}
    </div>
  );
}
